import React, { useState, useEffect } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

// Overlay de tutoriel générique (image + description par étape, navigation
// Précédent/Suivant) — pensé pour être réutilisé section par section sur la
// plateforme : chaque écran passe simplement son propre tableau `steps`
// ([{ image, title, legende, description }]).
function TutorialOverlay({ steps, onClose }) {
  const [index, setIndex] = useState(0);
  // Les images (Vercel Blob) mettent parfois un instant à être servies —
  // skeleton tant que l'image de l'étape courante n'a pas fini de charger.
  const [imgLoaded, setImgLoaded] = useState(false);

  useEffect(() => {
    setImgLoaded(false);
  }, [index]);

  if (!steps || steps.length === 0) return null;

  const step = steps[index];
  const isFirst = index === 0;
  const isLast = index === steps.length - 1;

  return (
    <div
      className="fixed inset-0 bg-black/60 z-[2000] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[92vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-4 px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-bold text-gray-900 leading-tight">
            {step.title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center text-gray-400 hover:text-gray-700 transition-colors flex-shrink-0"
          >
            <X size={16} />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-6 py-5">
          {step.image && (
            <div className="relative">
              {!imgLoaded && (
                <div className="absolute inset-0 rounded-xl bg-gray-100 animate-pulse min-h-[320px]" />
              )}
              <img
                src={step.image}
                alt={step.legende || step.title}
                onLoad={() => setImgLoaded(true)}
                className={`w-full rounded-xl border border-gray-100 transition-opacity duration-300 ${
                  imgLoaded ? "opacity-100" : "opacity-0"
                }`}
                style={{ minHeight: imgLoaded ? undefined : 320 }}
              />
            </div>
          )}
          {step.legende && (
            <p className="text-xs text-gray-400 text-center italic mt-1.5 mb-4">
              {step.legende}
            </p>
          )}
          <p
            className={`text-sm text-gray-600 leading-relaxed [&_strong]:text-[#840040] [&_strong]:font-semibold ${step.legende ? "" : "mt-4"}`}
          >
            {step.description}
          </p>
        </div>

        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100">
          <button
            type="button"
            onClick={() => setIndex((i) => i - 1)}
            disabled={isFirst}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${
              isFirst
                ? "opacity-0 pointer-events-none"
                : "border border-[#840040] text-[#840040] hover:bg-[#840040]/5"
            }`}
          >
            <ChevronLeft size={14} /> Précédent
          </button>

          <div className="flex items-center gap-1.5">
            {steps.map((_, i) => (
              <span
                key={i}
                className={`w-1.5 h-1.5 rounded-full transition-colors ${
                  i === index ? "bg-[#840040]" : "bg-gray-200"
                }`}
              />
            ))}
          </div>

          {isLast ? (
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2 rounded-xl text-sm font-semibold bg-[#840040] text-white hover:bg-[#6a0033] transition-colors"
            >
              Terminer
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setIndex((i) => i + 1)}
              className="flex items-center gap-1.5 px-5 py-2 rounded-xl text-sm font-semibold bg-[#840040] text-white hover:bg-[#6a0033] transition-colors"
            >
              Suivant <ChevronRight size={14} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default TutorialOverlay;
